import React, {useEffect, useState} from "react";
import {loadStripe} from "@stripe/stripe-js";
import {
    Elements,
} from "@stripe/react-stripe-js";
import {classNames, locale} from "../components/common/Helpers";
import PaymentInput from "./PaymentInput";
import {PulseLoader} from "react-spinners";
import PaymentInfoForm from "./PaymentInfoForm";
import {useDispatch, useSelector} from "react-redux";
import {obituaryEntity, store} from "./store/root";
import debounce from "lodash.debounce";

const publicKey = import.meta.env.VITE_APP_STRIPE_PUBLIC_KEY

export default function PaymentForm() {
    const currency = window.organizationCurrency || 'usd';
    const organizationStripeAccountId = window.organizationStripeAccountId;

    const [fieldErrors, setFieldErrors] = useState({});
    const [stripe, setStripe] = useState(null);
    const [amount, setAmount] = useState('');
    const [paymentTarget, setPaymentTarget] = useState({});
    const [forTab, setForTab] = useState('loved-one');
    const dispatch = useDispatch();
    const { obituaryList: searchResults, obituaryFetchAllLoading: searchLoading } = useSelector(state => state.obituaryReducer)

    useEffect(async () => {
        setStripe(await loadStripe(publicKey, {locale}));
    }, []);

    const inputForceCents = (e) => {
        let input = e.target.value;
        const onlyDigits = input.replace(/[^0-9]/g, '');
        const cents = parseInt(onlyDigits, 10) || 0;
        if (cents === 0) {
            e.target.value = '';
            return;
        }
        e.target.value = (cents / 100).toFixed(2);
    };

    const stripeElementsAppearance = {
        variables: {
            fontSizeBase: "17.25px", // Base font size
        },
    }

    const cents = parseInt(amount * 100);

    const search = debounce((input) => {
        dispatch(obituaryEntity.services.findAll({
            page: 1,
            per_page: 10,
            q: input,
            organization_id: window.organizationId,
        }));
    }, 300);

    const clearFieldError = (field) => () => {
        setFieldErrors((prev) => {
            return {...prev, [field]: null}
        })
    }

    if (!stripe) {
        return <div className="text-center m-10">
            <PulseLoader
                size={8}
                color={"#d1dbe6"}
            />
        </div>
    }

    let totalCents = window.paymentFees.show
        ? Math.round((cents + window.paymentFees.cents) / (1 - window.paymentFees.percent / 100)) || 0
        : cents;

    return (
        <Elements
            stripe={stripe}
            options={{
                currency,
                mode: cents == 0 ? 'setup' : 'payment',
                amount: cents > 0 ? totalCents : null,
                onBehalfOf: organizationStripeAccountId,
            }}
        >
            <div className="flex flex-col justify-center items-center">
                <div className="max-w-xl w-full m-5 mb-0 p-6 bg-white shadow-lg shadow-slate-200 rounded-lg">
                    <div className="md:flex gap-4 w-full">
                        <div className="flex-[2] md:mb-0 mb-7 justify-center">
                            <PaymentInput
                                type="number"
                                label="Amount"
                                name="amount"
                                placeholder="0.00"
                                required
                                onInput={inputForceCents}
                                onChange={(e) => setAmount(e.target.value)}
                                value={amount}
                                error={fieldErrors.amount}
                                onClearError={clearFieldError('amount')}
                                inputClass="text-4xl py-[13px]"
                                inputDescription={window.paymentFees.show && cents > 0 && `+${(totalCents - cents) / 100} transaction fee`}
                                autoFocus={true}
                            />
                        </div>

                        <div className="flex-[3]">
                            <PaymentInput
                                type="text"
                                label="For"
                                name="for"
                                placeholder={forTab === 'loved-one' ? "Enter loved one's name..." : "Enter reason for payment..."}
                                required
                                value={paymentTarget}
                                error={fieldErrors.for}
                                onClearError={clearFieldError('for')}
                                onChange={setPaymentTarget}
                                autoComplete={{
                                    idKey: 'id',
                                    labelKey: 'display_name',
                                    secondaryKey: 'formatted_date_of_death',
                                    imageKey: 'default_image',
                                    showLoading: forTab === 'loved-one',
                                    results: searchResults?.data,
                                    loading: searchLoading,
                                    search: forTab === 'loved-one' ? search : () => {},
                                }}
                                key={forTab}
                                renderBeforeInput={({open, focused}) => {
                                    const tabs = [
                                        {key: 'loved-one', label: 'Loved One'},
                                        {key: 'other', label: 'Other'},
                                    ];

                                    return (
                                        <div className="absolute z-10 right-2 -top-7 text-sm flex gap-1">
                                            {tabs.map((tab, index) => (
                                                <div key={tab.key}
                                                     className={classNames("relative", tab.key === forTab && "z-10")}>
                                                    {tab.key === forTab && (
                                                        <div className={classNames(
                                                            "transition-all w-full h-[22.5px] absolute top-0 rounded-t-md",
                                                            focused && "ring-[3px] ring-blue-200",
                                                            !focused && fieldErrors.for && 'ring-[1px] ring-red-600 h-[26px]',
                                                        )}></div>
                                                    )}

                                                    <button
                                                        tabIndex={1}
                                                        onClick={() => {
                                                            setForTab(tab.key)
                                                            setPaymentTarget({})
                                                        }}
                                                        className={classNames(
                                                            "relative transition-shadow py-0 pt-1 pb-[0.255rem] px-2",
                                                            tab.key === forTab ? "font-semibold text-slate-700 bg-white border border-b-0 cursor-default rounded-t-md" : "text-slate-400 hover:text-slate-600",
                                                            tab.key === forTab && focused && "border-blue-600",
                                                            tab.key === forTab && fieldErrors.for && !focused && "border-red-600",
                                                        )}
                                                    >
                                                        {tab.label}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )
                                }}
                            />
                            <p className="text-sm italic text-gray-500 mt-1">
                                Payment will be applied to this account.
                            </p>
                        </div>
                    </div>
                </div>

                <PaymentInfoForm
                    totalCents={totalCents}
                    amountCents={cents}
                    for={{
                        type: forTab === 'loved-one' ? 'obituary' : 'other',
                        text: paymentTarget?.display_name,
                        id: paymentTarget?.id,
                    }}
                    fieldErrors={fieldErrors}
                    setFieldErrors={setFieldErrors}
                />
            </div>
        </Elements>
    );
}
